import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import { useTranslate } from '@festina-life/ui/hooks'
import { Tabs } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useLayoutContext } from '../../../context/layoutContext'
import { useIsPersonaEditor } from '../../../uiModels/useIsPersonaEditor'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { countShadowedOperations } from '../../../workspace/shadowedOperationsHelpers'
import { LocationErrorsPanel } from '../../errorsPanel/LocationErrorsPanel'
import { BranchShadowedOperationsPanel } from '../../shadowedPanel/BranchShadowedOperationsPanel'
import { AdvancedManageBranch } from './advancedManageBranch/AdvancedManageBranch'
import { BranchConciseChangesetList } from './branchHistory/BranchConciseChangesetList'
import { BranchWorkspacesPanel } from './branchWorkspaces/BranchWorkspacesPanel'
import { ManageBranch } from './manageBranch/ManageBranch'
import { type BranchDetailsTab, tab } from './types'
import { useActiveElementTabsState } from './useActiveElementTabsState'

type Props = {
  branchKey: BranchKey
}

export const BranchDetailsTabs = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { persona } = useLayoutContext()
  const isPersonaEditor = useIsPersonaEditor()
  const shouldIncludeNonStandardBranchTab =
    branchKey.name !== STANDARD_BRANCH && isPersonaEditor
  const { branchShadowedTemplateOperations } = useOperationsContext()
  const shadowedCount = countShadowedOperations(
    branchShadowedTemplateOperations,
  )
  const shouldIncludeShadowedTab =
    shouldIncludeNonStandardBranchTab && shadowedCount > 0

  const tabItems: BranchDetailsTab[] = useMemo(
    () => [
      tab('manage', translate('branchDetailsPanel.tabs.manage.title')),
      tab('history', translate('branchDetailsPanel.tabs.history.title')),
      ...(shouldIncludeNonStandardBranchTab
        ? [
            ...(persona === 'ConfigurationAdmin'
              ? [
                  tab(
                    'advanced',
                    translate('branchDetailsPanel.tabs.advanced.title'),
                  ),
                ]
              : []),
            tab(
              'workspaces',
              translate('branchDetailsPanel.tabs.workspaces.title'),
            ),
            tab('errors', translate('branchDetailsPanel.tabs.errors.title')),
            tab('review', translate('branchDetailsPanel.tabs.review.title')),
            ...(shouldIncludeShadowedTab
              ? [
                  tab(
                    'shadowed',
                    translate('branchDetailsPanel.tabs.shadowed.title', {
                      count: shadowedCount,
                    }),
                  ),
                ]
              : []),
          ]
        : []),
    ],
    [
      persona,
      shouldIncludeNonStandardBranchTab,
      shouldIncludeShadowedTab,
      shadowedCount,
      translate,
    ],
  )
  const { selectedIndex, onTabChange } = useActiveElementTabsState(
    tabItems,
    'manage',
  )

  return (
    <Tabs
      items={tabItems}
      selectedIndex={selectedIndex}
      onChange={onTabChange}
      panelClassName="overflow-auto h-full"
      tabGroupClassName="h-full flex flex-col"
      tabItemsClassName="w-full flex-wrap overflow-x-hidden!"
      shouldMountInactive
    >
      {[
        <ManageBranch key="manage" />,
        <BranchConciseChangesetList
          key="history"
          variant="history"
          branchKey={branchKey}
          workspaceKey={undefined}
        />,
        ...(shouldIncludeNonStandardBranchTab
          ? [
              ...(persona === 'ConfigurationAdmin'
                ? [<AdvancedManageBranch key="advanced" />]
                : []),
              <BranchWorkspacesPanel key="workspaces" />,
              <LocationErrorsPanel key="errors" />,
              <BranchConciseChangesetList
                key="review"
                variant="review"
                branchKey={branchKey}
                workspaceKey={undefined}
              />,
              ...(shouldIncludeShadowedTab
                ? [<BranchShadowedOperationsPanel key="shadowed" />]
                : []),
            ]
          : []),
      ]}
    </Tabs>
  )
}
